/**
 * @file usePlanDocument.js
 * @description Data hook for the plan document workspace.
 *
 * Owns:
 *   ep 2  GET /api/v1/plans/{id}          — plan identity + config + amendment mode
 *   ep 35 GET /api/v1/plans/{id}/narrative — plan narrative
 *   GET /api/v1/roles?is_active=true       — role options
 *   GET /api/v1/segments?is_active=true    — segment options
 *   GET /api/v1/fiscal-periods             — fiscal period options
 *   PATCH /api/v1/plans/{id}              — explicit save only (no autosave)
 *
 * S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   localEdits and onFieldChange REMOVED from this hook.
 *   Each section component owns its own useState form initialized from plan prop.
 *   Save flow: PlanWorkspaceHeader Save → PlanDocumentCanvas.handleSave()
 *              → sectionRef.current.getValues() → usePlanDocument.save(fields)
 *              → one PATCH.
 *   This hook exposes: { document, loading, error, readOnly, save, saveState, load }
 *
 * @author data-fit LLC · DecisionFlow · S807 / S810
 * @changelog
 *   S807  Initial build
 *   S810  localEdits and onFieldChange REMOVED — section-local form state rewrite
 *   S884  Two completeness check fixes (modifierIsComplete):
 *     BUG-1 extension: rev_type_differentials completeness verifies that every
 *       revenue_type with bands has a non-empty entry in draftConfig.bandDates.
 *       effectiveDate is one value per revenue_type (not per band) — stored in
 *       bandDates parallel key. Backward compatible: absent bandDates passes
 *       (router falls back to plan.effectiveFrom).
 *     BUG-3 extension: draw_guarantee completeness rejects drawType='recoverable'.
 *       OI-S812-DRAW-RECOVERY-SCHEMA not resolved — recoverable draws blocked in UI;
 *       completeness check aligns to prevent false-complete indicator on legacy data.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Spine label map
// ---------------------------------------------------------------------------
const SPINE_LABELS = {
  revenue_bookings:       'Revenue / Bookings',
  transaction_commission: 'Transaction Commission',
  volume_quantity:        'Volume / Quantity',
  market_share:           'Market Share / Penetration',
  grr_nrr_retention:      'Retention (GRR / NRR)',
  mbo_objective:          'MBO / Objective',
  margin_profit:          'Margin / Profit',
  bonus_pool:             'Bonus Pool / Allocation',
  pharma_script:          'Pharma / Script Volume',
  // Legacy keys from old arch (plans_router) — display fallback only
  ARR:         'Revenue / Bookings',
  arr:         'Revenue / Bookings',
  revenue:     'Revenue / Bookings',
  mbo:         'MBO / Objective',
  nps:         'MBO / Objective',
  units:       'Volume / Quantity',
  transaction: 'Transaction Commission',
};

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

function normalizePlan(raw) {
  const primarySpine = raw.primarySpine ?? raw.primary_spine ?? null;
  const layer0Raw    = raw.layer0Config  ?? raw.layer0_config  ?? null;
  const layer1Raw    = raw.layer1Config  ?? raw.layer1_config  ?? null;
  const layer2Raw    = raw.layer2Config  ?? raw.layer2_config  ?? [];
  const temporal     = raw.temporalRelationship ?? {};

  const modifiers = buildModifierSummary(layer2Raw);
  const sourceTypeRaw = raw.sourceType ?? raw.source_type ?? 'webapp';
  const designSource  = sourceTypeRaw === 'library'
    ? 'library'
    : raw.crlRecommendationId
      ? 'crl'
      : 'custom';

  return {
    planId:        raw.planId       ?? raw.plan_id,
    planCode:      raw.planCode     ?? raw.plan_code     ?? '',
    planName:      raw.planName     ?? raw.plan_name     ?? '',
    version:       raw.version      ?? 1,
    planStatus:    raw.planStatus   ?? raw.plan_status   ?? 'draft',
    fiscalPeriodId: raw.fiscalPeriodId ?? raw.fiscal_period_id ?? null,
    roleId:        raw.roleId       ?? raw.role_id       ?? null,
    segmentId:     raw.segmentId    ?? raw.segment_id    ?? null,
    sourceType:    raw.sourceType   ?? raw.source_type   ?? 'webapp',
    description:   raw.description  ?? null,

    // Temporal (m_tc_01)
    effectiveFrom: raw.effectiveFrom ?? raw.effective_from ?? null,
    effectiveTo:   raw.effectiveTo   ?? raw.effective_to   ?? null,
    temporalRelationship:   temporal.relationship          ?? null,
    temporalDataAvailable:  temporal.temporalDataAvailable ?? false,

    // Amendment identity (AMD-01)
    requestedPlanId:   raw.requestedPlanId   ?? null,
    predecessorPlanId: raw.predecessorPlanId ?? null,
    supersededBy:      raw.supersededBy      ?? raw.superseded_by ?? null,
    revisionReason:    raw.revisionReason    ?? raw.revision_reason ?? null,

    // Governance timestamps + resolved names (ep 2 S808 enrichment)
    submittedBy:     raw.submittedBy     ?? raw.submitted_by      ?? null,
    submittedByName: raw.submittedByName ?? raw.submitted_by_name ?? null,
    submittedAt:     raw.submittedAt     ?? raw.submitted_at      ?? null,
    approvedBy:      raw.approvedBy      ?? raw.approved_by       ?? null,
    approvedByName:  raw.approvedByName  ?? raw.approved_by_name  ?? null,
    approvedAt:      raw.approvedAt      ?? raw.approved_at       ?? null,
    updatedAt:       raw.updatedAt       ?? raw.updated_at        ?? null,
    crlOverrideNote: raw.crlOverrideNote ?? null,

    // Amendment in flight
    amendmentInFlight: raw.amendmentInFlight ?? raw.amendment_in_flight ?? {
      hasAmendment: false, amendmentId: null,
      amendmentStatus: null, successorPlanId: null,
      initiatedBy: null, initiatedAt: null,
    },

    // Overview-computed fields
    primarySpine,
    primarySpineLabel: SPINE_LABELS[primarySpine] ?? primarySpine ?? 'Not configured',
    designSource,
    layer0Active: Boolean(layer0Raw && Object.keys(layer0Raw).length > 0),
    layer0Summary: layer0Raw?.summary ?? '',
    modifiers,

    // Raw config objects — passed through to sections via canvas adapter
    _layer0Raw:    layer0Raw,
    _layer1Raw:    layer1Raw,
    _layer2Raw:    Array.isArray(layer2Raw) ? layer2Raw : [],
    _settlementRaw: raw.settlementConfig ?? raw.settlement_config ?? null,
    _planComponent: raw.planComponent ?? raw.plan_component ?? null,  // S980: for cap completion check in deriveSectionStates
  };
}

function buildModifierSummary(layer2Config) {
  if (!layer2Config) return [];
  const configs = Array.isArray(layer2Config) ? layer2Config : [layer2Config];
  return configs.filter(Boolean).map((mod) => ({
    // S878 fix (OI-S877-UNKNOWN-MODIFIER-LABELS): EP2 returns layer2Config JSONB
    // with modifierType (camelCase) per D8 schema (arch spec §2.4). The fallback
    // chain must include modifierType before modifier_type and type.
    // Without it, mod.type and mod.modifier_type are both undefined and the
    // literal string 'Unknown' is used, producing 9x "Unknown" in PlanOverviewSection.
    type:    mod.modifierType ?? mod.type ?? mod.modifier_type ?? null,
    enabled: mod.enabled ?? mod.is_enabled       ?? false,
    summary: mod.summary ?? mod.modifier_summary ?? null,
  }));
}

function normalizeLayer0Config(raw) {
  if (!raw) {
    return { creditingMode: 'individual', layer0Type: null, attributionRules: null, isActive: false };
  }
  return {
    creditingMode:    raw.creditingMode    ?? raw.crediting_mode    ?? 'individual',
    layer0Type:       raw.layer0Type       ?? raw.layer0_type       ?? null,
    attributionRules: raw.attributionRules ?? raw.attribution_rules ?? null,
    isActive:         raw.isActive         ?? raw.is_active         ?? false,
  };
}

const DEFAULT_SECONDARY_MEASURES = [
  { key: 'mbo',      label: 'MBO / Objective', weight: 0, enabled: false },
  { key: 'nps',      label: 'NPS',             weight: 0, enabled: false },
  { key: 'adoption', label: 'Adoption',        weight: 0, enabled: false },
];

function normalizeLayer1Config(raw, primarySpineFromPlan) {
  const base = raw ?? {};
  const primarySpine = base.primarySpine ?? base.primary_spine ?? primarySpineFromPlan ?? '';

  const rawWeights = base.weights ?? {};
  let weights;
  if (rawWeights.primary != null) {
    // S814 canonical shape: {primary, mbo, nps, adoption} — whole integers 0–100.
    // D9: server converts to/from fractions. UI always works in whole integers.
    // Derive legacy UI display shape from canonical values for VariableCompAllocation.
    const primaryVal  = Math.round(Number(rawWeights.primary)  ?? 0);
    const mboVal      = Math.round(Number(rawWeights.mbo)      ?? 0);
    const npsVal      = Math.round(Number(rawWeights.nps)      ?? 0);
    const adoptionVal = Math.round(Number(rawWeights.adoption) ?? 0);
    weights = {
      primarySpineLabel:  SPINE_LABELS[primarySpine] ?? primarySpine ?? 'Primary spine',
      primarySpineWeight: primaryVal,
      secondaryMeasures: [
        { key: 'mbo',      label: 'MBO / Objective', weight: mboVal,      enabled: mboVal > 0 },
        { key: 'nps',      label: 'NPS',             weight: npsVal,      enabled: npsVal > 0 },
        { key: 'adoption', label: 'Adoption',        weight: adoptionVal, enabled: adoptionVal > 0 },
      ],
    };
  } else if (rawWeights.secondaryMeasures && Array.isArray(rawWeights.secondaryMeasures)) {
    // Legacy shape from EP7 seed or old plans: {primarySpineWeight, secondaryMeasures[{weight}]}
    // secondaryMeasures[].weight is already a whole integer — do NOT multiply by 100.
    weights = {
      primarySpineLabel:  rawWeights.primarySpineLabel  ?? (SPINE_LABELS[primarySpine] ?? primarySpine ?? ''),
      primarySpineWeight: rawWeights.primarySpineWeight ?? 100,
      secondaryMeasures:  rawWeights.secondaryMeasures,
    };
  } else {
    // DB fraction shape: {mbo: 0.3, nps: 0.0, adoption: 0.0} — multiply by 100.
    const secondaryMeasures = DEFAULT_SECONDARY_MEASURES.map((measure) => {
      const rawVal = rawWeights[measure.key];
      const weight = rawVal != null ? Math.round(Number(rawVal) * 100) : 0;
      return { ...measure, weight, enabled: weight > 0 };
    });
    const secondaryTotal = secondaryMeasures.reduce((s, m) => s + (m.enabled ? m.weight : 0), 0);
    const primarySpineWeight = Math.max(0, 100 - secondaryTotal);
    // BUG-03 FIX: always resolve label via SPINE_LABELS for old-format plans
    // that stored raw keys (e.g. 'revenue_bookings') without a label string.
    weights = {
      primarySpineLabel:  SPINE_LABELS[primarySpine] ?? primarySpine ?? 'Primary spine',
      primarySpineWeight,
      secondaryMeasures,
    };
  }

  return {
    primarySpine,
    weights,
    rateTableRef: base.rateTableRef ?? base.rate_table_ref ?? {
      rateTableId: null, version: null, versionLabel: null,
    },
    quotaSemantics: {
      measurementBasis:    base.quotaSemantics?.measurementBasis    ?? base.measurement_basis   ?? '',
      currencyCode:        base.quotaSemantics?.currencyCode        ?? base.quota_currency_code ?? '',
      seasonalityProfile:  base.quotaSemantics?.seasonalityProfile  ?? null,
      measurementScope:    base.quotaSemantics?.measurementScope     ?? null,
    },
  };
}

function normalizeLayer2Config(rawArray) {
  // D8 — canonical manifest shape: modifierType, enabled, entityRefs, draftConfig
  // entityId always a string (bigint IDs — JS precision safety)
  const arr = Array.isArray(rawArray) ? rawArray : [];
  return {
    modifiers: arr.map((mod) => ({
      modifierType: mod.modifierType ?? mod.modifier_type ?? mod.type ?? '',
      enabled:      mod.enabled      ?? mod.is_enabled    ?? false,
      entityRefs:   Array.isArray(mod.entityRefs) ? mod.entityRefs : [],
      draftConfig:  mod.draftConfig  ?? null,
    })),
  };
}

function normalizeSettlementConfig(raw) {
  if (!raw) {
    return {
      policy: 'none',
      trueUpFrequency: null,
      comparisonBasis: null,
      lookbackWindowMonths: null,
      carryForwardRules: null,
      yearEndTreatment: null,
      customTerms: null,
    };
  }
  return {
    policy:               raw.policy               ?? 'none',
    trueUpFrequency:      raw.trueUpFrequency       ?? raw.true_up_frequency      ?? null,
    comparisonBasis:      raw.comparisonBasis       ?? raw.comparison_basis        ?? null,
    lookbackWindowMonths: raw.lookbackWindowMonths  ?? raw.lookback_window_months  ?? null,
    carryForwardRules:    raw.carryForwardRules     ?? raw.carry_forward_rules     ?? null,
    yearEndTreatment:     raw.yearEndTreatment      ?? raw.year_end_treatment      ?? null,
    customTerms:          raw.customTerms           ?? raw.custom_terms            ?? null,
  };
}

function normalizeScopeRules(raw) {
  const scope = raw?.scopeRules ?? raw?.scope_rules ?? raw?.layer0Config?.scopeRules ?? {};
  return {
    roleCriteria:    scope.roleCriteria    ?? scope.role_criteria    ?? [],
    segmentCriteria: scope.segmentCriteria ?? scope.segment_criteria ?? [],
    buCriteria:      scope.buCriteria      ?? scope.bu_criteria      ?? [],
    regionCriteria:  scope.regionCriteria  ?? scope.region_criteria  ?? [],
    effectiveFrom:   scope.effectiveFrom   ?? scope.effective_from   ?? null,
    effectiveTo:     scope.effectiveTo     ?? scope.effective_to     ?? null,
  };
}

function normalizeAssignmentPopulation(raw) {
  return {
    totalCount:                    raw?.populationCount ?? raw?.population_count ?? null,
    resolvedAt:                    raw?.populationFreshnessAt ?? null,
    freshnessStatus:               'stale',
    additionsSinceLastResolution:  0,
    removalsSinceLastResolution:   0,
  };
}

function normalizeLineage(plan) {
  return [{
    version:     plan.version     ?? 1,
    planStatus:  plan.planStatus  ?? 'draft',
    effectiveFrom: plan.effectiveFrom ?? null,
    effectiveTo:   plan.effectiveTo   ?? null,
    eventType:   plan.planStatus === 'approved' ? 'approved' : 'created',
    eventAt:     plan.approvedAt ?? plan.updatedAt ?? new Date().toISOString(),
  }];
}

function normalizeIdentity(raw, refData) {
  const roleId    = raw.roleId    ?? raw.role_id    ?? null;
  const segmentId = raw.segmentId ?? raw.segment_id ?? null;
  const fpId      = raw.fiscalPeriodId ?? raw.fiscal_period_id ?? null;
  const temporal  = raw.temporalRelationship ?? {};

  const role    = refData.roles.find((r) => r.roleId    === roleId)    ?? null;
  const segment = refData.segments.find((s) => s.segmentId === segmentId) ?? null;
  const fp      = refData.fiscalPeriods.find((p) => p.fiscalPeriodId === fpId) ?? null;

  return {
    planId:            String(raw.planId ?? raw.plan_id ?? ''),
    planCode:          raw.planCode ?? raw.plan_code ?? '',
    planName:          raw.planName ?? raw.plan_name ?? '',
    roleId,
    roleName:          role?.roleName ?? '',
    segmentId,
    segmentName:       segment?.segmentName ?? '',
    fiscalPeriodId:    fpId,
    fiscalPeriodLabel: fp?.periodLabel ?? '',
    effectiveFrom:     raw.effectiveFrom ?? raw.effective_from ?? null,
    effectiveTo:       raw.effectiveTo   ?? raw.effective_to   ?? null,
    version:           raw.version ?? 1,
    planStatus:        raw.planStatus ?? raw.plan_status ?? 'draft',
    temporalRelationship: temporal.relationship ?? 'draft_not_governing',
  };
}

function normalizePopulation(raw) {
  return {
    count:       raw.populationCount ?? raw.population_count ?? 0,
    freshnessAt: raw.populationFreshnessAt ?? new Date().toISOString(),
    status:      'stale',
  };
}

// ---------------------------------------------------------------------------
// Completion helpers — S812 canonical
// ---------------------------------------------------------------------------

/**
 * isCompleteLike — a section with a 'warning' advisory (e.g., open-ended date)
 * still counts as complete for Overview and Review & Submit derivation.
 * Must be used for ALL cross-section derivations. Never use state === 'complete'
 * directly for these checks.
 */
const isCompleteLike = (state) => state === 'complete' || state === 'warning';

/** nonEmpty — true when value is a non-null, non-empty-string, non-whitespace string */
const nonEmpty = (v) => v != null && String(v).trim().length > 0;

/**
 * modifierIsComplete — per-modifier completion check per S812 per-modifier specs.
 * Operates on the raw layer2Config manifest element (D8 shape).
 * Returns true only when all required fields for the modifier type are present.
 */
function modifierIsComplete(mod, planComponent) {
  // If there are entityRefs (materialized), treat as complete — relational row exists.
  // The relational row's NN constraints are the source of truth for completeness.
  if (Array.isArray(mod.entityRefs) && mod.entityRefs.length > 0) return true;

  // If only draftConfig (not yet materialized), check required fields by modifierType.
  const dc = mod.draftConfig;
  if (!dc) return false;

  switch (mod.modifierType) {
    case 'cap':
      // S980: cap materializes to plan_component.max_period_payout (not draftConfig).
      // When deployed from CPL seed, draftConfig={} and entityRefs=[] but max_period_payout
      // is written to plan_component. Check planComponent as fallback.
      // Same logic as OI-S814-CAP-DISPLAY-AFTER-MATERIALIZE in PayoutAdjustmentsSection.
      const capPayout = dc?.maxPeriodPayout ??
        planComponent?.maxPeriodPayout ?? planComponent?.max_period_payout ?? null;
      const capCurrency = nonEmpty(dc?.maxPeriodPayoutCurrency) ||
        nonEmpty(planComponent?.maxPeriodPayoutCurrency) ||
        nonEmpty(planComponent?.max_period_payout_currency);
      return capPayout != null && Number(capPayout) > 0 && capCurrency;
    case 'kicker':
      // S891 Wave 1A: section sends { plans: [{planName, appliesTo, effectiveFrom,
      // rules: [{metric, operator, thresholdValue, multiplierValue}]}] }.
      // Previous check read flat dc.planName — always undefined against array wrapper.
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
             dc.plans.every((p) =>
               nonEmpty(p.planName) && nonEmpty(p.appliesTo) && nonEmpty(p.effectiveFrom) &&
               Array.isArray(p.rules) && p.rules.length > 0 &&
               p.rules.every((r) => nonEmpty(r.metric) && nonEmpty(r.operator) &&
                 r.thresholdValue != null && r.multiplierValue != null));
    case 'clawback':
      // S891 Wave 1A: section sends { policies: [{policyName, protectionWindowDays,
      // clawbackPct, effectiveFrom}] }.
      // Previous check read flat dc.policyName — always undefined.
      return Array.isArray(dc.policies) && dc.policies.length > 0 &&
             dc.policies.every((p) =>
               nonEmpty(p.policyName) && p.protectionWindowDays != null &&
               p.clawbackPct != null && nonEmpty(p.effectiveFrom));
    case 'spiff':
      // S891 Wave 1A: section sends { plans: [{planName, triggerModel,
      // effectiveFrom, payoutCurrency}] }.
      // Previous check read flat dc.planName — always undefined.
      // S892 Wave 3: OI-S812-SPIFF-TIER-SCHEMA CLOSED.
      //   Phase 1 (OD-13 / AD-56): UI sends one rule per plan with a tiers[]
      //   array. Completeness now requires:
      //     - plans[].rules non-empty (at least one rule)
      //     - Each rule has at least one tier with minQty and payoutAmount present.
      //   max_qty is intentionally not required — null = open-ended top tier (m075 DDL).
      //   entityRefs short-circuit (line above) still applies: if a spiff_plan row
      //   exists (materialized), the DB NN constraints are the source of truth.
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
             dc.plans.every((p) =>
               nonEmpty(p.planName) && nonEmpty(p.triggerModel) &&
               nonEmpty(p.effectiveFrom) && nonEmpty(p.payoutCurrency) &&
               Array.isArray(p.rules) && p.rules.length > 0 &&
               p.rules.every((r) =>
                 Array.isArray(r.tiers) && r.tiers.length > 0 &&
                 r.tiers.some((t) => t.minQty != null && t.payoutAmount != null)));
    case 'draw_guarantee':
      // S891 Wave 1A: section sends { plans: [{planName, drawType, advanceAmount,
      // advanceCurrency, effectiveFrom, yearEndTreatment, recoverySchedule}] }.
      // S891 Wave 2: OI-S812-DRAW-RECOVERY-SCHEMA closed. Recoverable now allowed.
      // Recoverable draws require recoverySchedule.durationPeriods and
      // recoverySchedule.recoveryCapPct to be populated — these satisfy G-23
      // (recovery_schedule_json IS NOT NULL) when router serializes them.
      // Non-recoverable: no recovery schedule required.
      // OD-6 (S884): yearEndTreatment required — draw_plan.year_end_treatment NN.
      return Array.isArray(dc.plans) && dc.plans.length > 0 &&
             dc.plans.every((p) => {
               if (!nonEmpty(p.planName) || !nonEmpty(p.drawType)) return false;
               if (p.advanceAmount == null || Number(p.advanceAmount) <= 0) return false;
               if (!nonEmpty(p.advanceCurrency) || !nonEmpty(p.effectiveFrom)) return false;
               if (!nonEmpty(p.yearEndTreatment)) return false;
               if (p.drawType === 'recoverable') {
                 const rs = p.recoverySchedule ?? {};
                 return rs.durationPeriods != null && rs.durationPeriods !== '' &&
                        rs.recoveryCapPct != null && rs.recoveryCapPct !== '';
               }
               return true;
             });
    case 'contest':
      // contest uses dc.contests[] — already correct in section; hook was also
      // correct. No shape change needed.
      return nonEmpty(dc.contestName) && nonEmpty(dc.contestCode) &&
             nonEmpty(dc.startDate) && nonEmpty(dc.endDate) &&
             nonEmpty(dc.eligibilityType) && nonEmpty(dc.rankMetric) &&
             nonEmpty(dc.tieBreakRule) &&
             Array.isArray(dc.prizes) && dc.prizes.length > 0;
    case 'non_cash':
      return Array.isArray(dc.awardTypeRefs) && dc.awardTypeRefs.length > 0;
    case 'multi_year_deal':
      // S891 Wave 1A: section sends { policies: [{payoutBasis, maxContractYears,
      // effectiveFrom, amortizationPeriods}] }.
      // Previous check read flat dc.payoutBasis — always undefined.
      return Array.isArray(dc.policies) && dc.policies.length > 0 &&
             dc.policies.every((p) =>
               nonEmpty(p.payoutBasis) && p.maxContractYears != null &&
               nonEmpty(p.effectiveFrom) &&
               (p.payoutBasis !== 'amortized' || p.amortizationPeriods != null));
    case 'rev_type_differentials':
      // C-12 S873: bands is a dict keyed by revenue_type_code — correct shape,
      // no change to dict check. BUG-1 FIX (S884): bandDates check retained.
      // Shape unchanged — section and router both use dict, not array.
      return typeof dc.bands === 'object' && !Array.isArray(dc.bands) &&
             dc.bands !== null &&
             Object.values(dc.bands).some((bl) => Array.isArray(bl) && bl.length > 0) &&
             Object.keys(dc.bands).every((code) => {
               const bl = dc.bands[code];
               if (!Array.isArray(bl) || bl.length === 0) return true;
               const bd = dc.bandDates ?? {};
               return !bd[code] || nonEmpty(bd[code]);
             });
    default:
      return false;
  }
}

/**
 * deriveSectionStates — derives completion state for each section from rawPlan
 * (server state only). Returns a plain object keyed by section key.
 *
 * INTENTIONAL: completion derives from rawPlan (server state) only.
 * Local edits that have not been saved do NOT update completion indicators.
 * Completion updates after Save + EP2 refetch. This is correct per spec Decision #14.
 * BUG-08 was filed against this behavior — it is NOT a bug. Do not "fix" it.
 *
 * Section keys: overview, identity, credit-allocation, measurement-economics,
 *               payout-adjustments, settlement, review-governance
 * assignment-scope is REMOVED — D3 locked S811.
 *
 * Completion states: 'complete' | 'incomplete' | 'optional' | 'warning'
 *   complete  — green ✓
 *   incomplete — grey ○
 *   optional  — grey –  (section not required; having nothing configured is valid)
 *   warning   — yellow ! (complete with advisory — still counts as complete for derivations)
 */
function deriveSectionStates(plan) {
  // ── Plan Basics (identity) ───────────────────────────────────────────────
  // BUG-02 FIX: effectiveFrom is required (NN in DDL r60). Was missing from check.
  const identityComplete =
    nonEmpty(plan.planName) &&
    plan.roleId    != null &&
    plan.segmentId != null &&
    plan.effectiveFrom != null;

  // D1: effectiveTo is always optional. Both open-ended AND set produce a warning.
  // Neither blocks Complete. Neither blocks Submit.
  const identityState = identityComplete ? 'warning' : 'incomplete';
  // Note: 'warning' is the final state for Plan Basics whenever the base fields
  // are complete — open-ended date advisory OR expiry advisory always applies.
  // Once the UI implements explicit "I understand" dismissal, this can resolve to
  // 'complete'. For now, warning is correct: it counts as completeLike for all
  // derivations and surfaces the advisory in the navigator.

  // ── Crediting (credit-allocation) ────────────────────────────────────────
  // BUG-12 FIX: non-individual mode requires layer0Type AND attributionRules non-null.
  // Was: Boolean(_layer0Raw) which is truthy even when layer0Type is null.
  const creditingState = (() => {
    const mode = plan._layer0Raw?.creditingMode ?? plan._layer0Raw?.crediting_mode ?? 'individual';
    if (!mode || mode === 'individual') return 'complete';
    if (plan._layer0Raw == null) return 'incomplete';
    return (plan._layer0Raw?.layer0Type    != null &&
            plan._layer0Raw?.attributionRules != null)
      ? 'complete' : 'incomplete';
  })();

  // ── Performance & Rates (measurement-economics) ──────────────────────────
  // BUG-10 partial: layer1 completion fully specified.
  // Weights in rawPlan are whole integers 0–100 (D9 — server converts before returning).
  const perfRatesState = (() => {
    if (plan._layer1Raw == null) return 'incomplete';

    // Weights: read from rawPlan layer1Config shape (whole integers 0–100)
    const l1 = plan._layer1Raw;
    const w = l1.weights ?? {};
    // Support both new shape (primary/mbo/nps/adoption) and old shape (primarySpineWeight+secondaryMeasures)
    let weightsValid = false;
    if (w.primary != null) {
      // New canonical shape (D9): primary, mbo, nps, adoption — all whole integers
      const sum = (w.primary ?? 0) + (w.mbo ?? 0) + (w.nps ?? 0) + (w.adoption ?? 0);
      weightsValid = sum === 100 &&
        [w.primary, w.mbo, w.nps, w.adoption].every((v) => v >= 0 && v <= 100);
    } else if (w.primarySpineWeight != null) {
      // Legacy normalizer shape — compute sum
      const secondary = (w.secondaryMeasures ?? [])
        .filter((m) => m.enabled).reduce((s, m) => s + (m.weight ?? 0), 0);
      weightsValid = (w.primarySpineWeight + secondary) === 100;
    }

    const isBonus  = plan.primarySpine === 'bonus_pool';
    const rateOk   = isBonus || (l1.rateTableRef?.rateTableId != null);
    const basisOk  = nonEmpty(l1.quotaSemantics?.measurementBasis);
    const currOk   = nonEmpty(l1.quotaSemantics?.currencyCode);

    if (!weightsValid || !rateOk || !basisOk || !currOk) return 'incomplete';

    // Warning: rate table selected but not date-compatible
    const compat = l1.rateTableRef?.effectiveDateCompatibility;
    return (compat && compat !== 'compatible') ? 'warning' : 'complete';
  })();

  // ── Adjustments (payout-adjustments) ─────────────────────────────────────
  const adjustmentsState = (() => {
    const configs = Array.isArray(plan._layer2Raw) ? plan._layer2Raw : [];
    const enabled = configs.filter((m) => m.enabled ?? m.is_enabled ?? false);
    if (enabled.length === 0) return 'optional';
    return enabled.every((m) => modifierIsComplete(m, plan._planComponent)) ? 'complete' : 'incomplete';
  })();

  // ── True-Up (settlement) ─────────────────────────────────────────────────
  const trueUpState = (() => {
    const s = plan._settlementRaw;
    const policy = s?.policy ?? 'none';
    if (!s || policy === 'none') return 'optional';
    if (policy === 'custom') {
      return nonEmpty(s.customTerms) ? 'complete' : 'incomplete';
    }
    if (policy === 'true_up') {
      return (s.trueUpFrequency && s.comparisonBasis &&
              s.lookbackWindowMonths && s.carryForwardRules && s.yearEndTreatment)
        ? 'complete' : 'incomplete';
    }
    return 'incomplete';
  })();

  // ── Overview & Review & Submit ────────────────────────────────────────────
  // BUG-01 FIX: overview was hardcoded 'complete'. Now derived from three core sections.
  // BUG-10 FIX: review-governance was hardcoded 'incomplete'. Now derived correctly.
  // Both use isCompleteLike so that warning state (e.g., identity with date advisory)
  // still counts as complete for these derivations.
  const coreComplete =
    isCompleteLike(identityState) &&
    isCompleteLike(creditingState) &&
    isCompleteLike(perfRatesState);

  const overviewState    = coreComplete ? 'complete' : 'incomplete';
  const reviewState      = coreComplete ? 'complete' : 'incomplete';

  // assignment-scope is REMOVED — D3 locked S811. Do not add it back.
  return {
    'overview':              overviewState,
    'identity':              identityState,
    'credit-allocation':     creditingState,
    'measurement-economics': perfRatesState,
    'payout-adjustments':    adjustmentsState,
    'settlement':            trueUpState,
    'review-governance':     reviewState,
  };
}

function normalizeRoles(raw) {
  const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.roles ?? raw?.items ?? [];
  return list.map((r) => ({
    roleId:   r.roleId   ?? r.role_id,
    roleName: r.roleName ?? r.role_name ?? r.name ?? '',
    roleCode: r.roleCode ?? r.role_code ?? r.code ?? '',
  }));
}

function normalizeSegments(raw) {
  const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.segments ?? raw?.items ?? [];
  return list.map((s) => ({
    segmentId:   s.segmentId   ?? s.segment_id,
    segmentName: s.segmentName ?? s.segment_name ?? s.name ?? '',
  }));
}

function normalizeFiscalPeriods(raw) {
  const list = Array.isArray(raw) ? raw : raw?.data ?? raw?.fiscalPeriods ?? raw?.fiscal_periods ?? raw?.items ?? [];
  return list.map((p) => ({
    fiscalPeriodId: p.fiscalPeriodId ?? p.fiscal_period_id ?? p.id,
    periodLabel:    p.periodLabel    ?? p.period_label     ?? p.label ?? '',
    periodStart:    p.periodStart    ?? p.period_start_date ?? p.start ?? '',
    periodEnd:      p.periodEnd      ?? p.period_end_date   ?? p.end   ?? '',
  }));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export default function usePlanDocument(planId) {
  const [rawPlan,       setRawPlan]       = useState(null);
  const [narrative,     setNarrative]     = useState({ tier1Summary: null, tier2Constructs: [], tier3Coverage: null });
  const [intelligence,  setIntelligence]  = useState(null);
  const [roles,         setRoles]         = useState([]);
  const [segments,      setSegments]      = useState([]);
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  // saveState.status: 'idle' | 'saving' | 'saved' | 'error'
  // S810: 'unsaved' removed — sections own local edit state; hook only tracks network state
  const [saveState,     setSaveState]     = useState({
    status: 'idle', lastSavedAt: null, errorMessage: null,
  });

  const mountedRef = useRef(true);
  const abortRef   = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    if (!planId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const [planRes, rolesRes, segmentsRes, periodsRes] = await Promise.all([
        fetch(`/api/v1/plans/${planId}`,        { credentials: 'include', signal: controller.signal }),
        fetch('/api/v1/roles?is_active=true',    { credentials: 'include', signal: controller.signal }),
        fetch('/api/v1/segments?is_active=true', { credentials: 'include', signal: controller.signal }),
        fetch('/api/v1/fiscal-periods',          { credentials: 'include', signal: controller.signal }),
      ]);

      if (!mountedRef.current || controller.signal.aborted) return;

      if (!planRes.ok) {
        const errBody = await planRes.json().catch(() => ({}));
        throw new Error(errBody.detail || `Unable to load plan (${planRes.status}).`);
      }

      const [planData, rolesData, segmentsData, periodsData] = await Promise.all([
        planRes.json(),
        rolesRes.ok ? rolesRes.json() : Promise.resolve([]),
        segmentsRes.ok ? segmentsRes.json() : Promise.resolve([]),
        periodsRes.ok ? periodsRes.json() : Promise.resolve([]),
      ]);

      if (!mountedRef.current || controller.signal.aborted) return;

      setRawPlan(planData);
      setRoles(normalizeRoles(rolesData));
      setSegments(normalizeSegments(segmentsData));
      setFiscalPeriods(normalizeFiscalPeriods(periodsData));

      // Non-blocking narrative fetch (ep 35)
      fetch(`/api/v1/plans/${planId}/narrative?persona=ca1`, { credentials: 'include', signal: controller.signal })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (mountedRef.current && !controller.signal.aborted && data) setNarrative(data); })
        .catch(() => {});

      // Non-blocking intelligence fetch (ep 62)
      fetch(`/api/v1/plans/${planId}/intelligence`, { credentials: 'include', signal: controller.signal })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => { if (mountedRef.current && !controller.signal.aborted && data) setIntelligence(data); })
        .catch(() => {});

    } catch (err) {
      if (!mountedRef.current || err.name === 'AbortError') return;
      setError(err.message || 'Unable to load plan.');
    } finally {
      if (mountedRef.current && !controller.signal.aborted) setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    load();
    return () => {
      abortRef.current?.abort();
    };
  }, [load]);

  // ---------------------------------------------------------------------------
  // save(fields) — called by PlanDocumentCanvas.handleSave() with values from
  // sectionRef.current.getValues(). fields is a plain object matching the
  // PlanPatchBody schema — no dotted paths, no localEdits merging.
  // Rule 9: use RETURNING via the existing ep2b handler; do NOT re-query.
  // ---------------------------------------------------------------------------
  const save = useCallback(async (fields) => {
    if (!planId || !rawPlan) return;
    setSaveState({ status: 'saving', lastSavedAt: null, errorMessage: null });
    try {
      // S814 — assemble full PATCH body per spec §1.2b.
      // The server requires planName, roleId, segmentId, effectiveFrom on every PATCH.
      // Section getValues() returns only its own fields. The hook merges identity
      // from rawPlan (authoritative server state) so every Save carries the full envelope.
      // Fields from the section override rawPlan values — the section's intent wins.
      const identity = {
        planName:      rawPlan.planName     ?? rawPlan.plan_name     ?? '',
        roleId:        rawPlan.roleId       ?? rawPlan.role_id       ?? null,
        segmentId:     rawPlan.segmentId    ?? rawPlan.segment_id    ?? null,
        effectiveFrom: rawPlan.effectiveFrom ?? rawPlan.effective_from ?? null,
        effectiveTo:   rawPlan.effectiveTo   ?? rawPlan.effective_to   ?? null,
      };
      const body = { ...identity, ...fields };

      const response = await fetch(`/api/v1/plans/${planId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!mountedRef.current) return;
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detailMsg = typeof errBody.detail === 'string'
          ? errBody.detail
          : Array.isArray(errBody.detail)
            ? errBody.detail.map((e) => e.msg || JSON.stringify(e)).join('; ')
            : errBody.error?.message ?? errBody.error?.code ?? `Save failed (${response.status}).`;
        throw new Error(detailMsg);
      }
      // After a successful PATCH (ep 2b RETURNING gives planId/planStatus/version/updatedAt only),
      // refetch EP2 to rebuild rawPlan from the full server state. This is correct per Rule 9
      // and the S812 spec: "After receiving this response, the UI hook MUST refetch EP2 to
      // rebuild rawPlan and update completion indicators."
      // Do NOT merge the PATCH body into rawPlan — the PATCH body is the UI's intent, not
      // the server's authoritative state (e.g. weight unit conversion, materialized entityRefs).
      const savedAt = new Date().toISOString();
      setSaveState({ status: 'saved', lastSavedAt: savedAt, errorMessage: null });
      // Trigger EP2 refetch via load() — completion indicators update from server state.
      load();
    } catch (err) {
      if (!mountedRef.current) return;
      setSaveState({ status: 'error', lastSavedAt: null, errorMessage: err.message || 'Save failed.' });
    }
  // load is included in deps because save() calls load() after a successful PATCH
  // to trigger the mandatory EP2 refetch (S812 spec — completion must update from server state).
  }, [planId, rawPlan, load]);

  // ---------------------------------------------------------------------------
  // Build document object — no localEdits merge (S810)
  // ---------------------------------------------------------------------------
  const document = (() => {
    if (!rawPlan) return null;

    const plan         = normalizePlan(rawPlan);
    const refData      = { roles, segments, fiscalPeriods };
    const identity     = normalizeIdentity(rawPlan, refData);
    const layer1Config = normalizeLayer1Config(plan._layer1Raw, plan.primarySpine);
    // deriveSectionStates reads from plan (rawPlan normalized shape) only —
    // server state drives completion; local edits do not affect it (BUG-08 intentional).
    const sections     = deriveSectionStates(plan);

    return {
      // Core
      plan,
      identity,
      overview: plan,
      sections,

      // Reference data (for section dropdowns)
      roles,
      segments,
      fiscalPeriods,

      // Narrative (ep 35)
      narrative,

      // Overview population
      population: normalizePopulation(rawPlan),

      // CRL state (stub — ep 16 fires post-creation)
      crlState: { recommendation: {}, currentConfig: {} },

      // Section-specific normalized configs — used to INITIALIZE section form state
      layer0Config:     normalizeLayer0Config(plan._layer0Raw),
      layer1Config,     // already computed above for deriveSectionStates
      layer2Config:     normalizeLayer2Config(plan._layer2Raw),
      settlementConfig: normalizeSettlementConfig(plan._settlementRaw),

      // Assignment scope
      scopeRules:           normalizeScopeRules(rawPlan),
      assignmentPopulation: normalizeAssignmentPopulation(rawPlan),
      availableBUs:         [],
      availableRegions:     [],

      // Governance section
      lineage:      normalizeLineage(plan),
      auditSummary: null,

      // Amendment
      amendmentInFlight: plan.amendmentInFlight,

      // Plan intelligence (ep 62)
      intelligence,

      // Plan component row — exposes max_period_payout / max_period_payout_currency
      // for PayoutAdjustmentsSection cap initFromLayer2 seed (OI-S815-USEPLANDOCUMENT-PLANCOMPONENT).
      // EP2 (plan_governance_router S814) returns planComponent alongside the plan row.
      // Dual alias: camelCase (normalized EP2) and snake_case (raw fallback).
      planComponent: rawPlan.planComponent ?? rawPlan.plan_component ?? null,
    };
  })();

  const readOnly = Boolean(
    rawPlan && rawPlan.planStatus !== 'draft' && rawPlan.planStatus !== 'pending_approval'
  );

  // S810: returns save() not onFieldChange. saveState reflects network state only.
  return { document, loading, error, readOnly, save, saveState, load };
}
